import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Rocket, MapPin, Car } from "lucide-react";
import api from "@/lib/api";
import { flagImg } from "@/lib/places";
import { track } from "@/lib/track";

function Slide({ l, onOpen }) {
  const img = l.images?.[0] || l.data?.logo;
  const logo = l.data?.logo;
  const flag = flagImg(l.country);
  return (
    <button data-testid={`sponsored-slide-${l.id}`} onClick={onOpen} className="relative w-full shrink-0 snap-center h-52 rounded-3xl overflow-hidden shadow-lg text-left">
      <img src={img} alt={l.title} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
      <span className="absolute top-3 right-3 w-11 h-11 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-md" title="Sponsorizuar"><Rocket className="w-5 h-5" /></span>
      <div className="absolute inset-x-0 bottom-0 bg-black/70 backdrop-blur-sm px-3 py-2.5 flex items-center gap-2.5">
        {logo && <img src={logo} alt="" className="w-11 h-11 rounded-full border-2 border-yellow-400 object-cover bg-white shrink-0" />}
        {logo && <span className="w-0.5 h-9 bg-emerald-500 rounded shrink-0" />}
        <div className="flex-1 min-w-0 text-white">
          <div className="font-extrabold text-base leading-tight truncate">{l.title}</div>
          <div className="text-xs text-white/85 flex items-center gap-1 truncate"><MapPin className="w-3.5 h-3.5 shrink-0" />{l.city}</div>
        </div>
        {flag && <img src={flag} alt={l.country} className="w-7 h-5 object-cover rounded-[3px] shrink-0" />}
        {l.distance != null && <span className="text-white font-bold text-sm flex items-center gap-1 border-l border-white/40 pl-2 shrink-0"><Car className="w-4 h-4" />{l.distance < 1 ? `${Math.round(l.distance * 1000)} m` : `${Math.round(l.distance)} km`}</span>}
      </div>
    </button>
  );
}

export function SponsoredSlider({ coords }) {
  const nav = useNavigate();
  const ref = useRef(null);
  const hold = useRef(0);
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const q = coords ? `?lat=${coords.lat}&lng=${coords.lng}` : "";
    api.get(`/sponsored${q}`).then((r) => setItems(r.data)).catch(() => {});
  }, [coords?.lat, coords?.lng]);

  useEffect(() => {
    if (items.length < 2) return undefined;
    const id = setInterval(() => {
      const el = ref.current;
      if (!el || Date.now() < hold.current || document.hidden) return;
      const n = (Math.round(el.scrollLeft / el.clientWidth) + 1) % items.length;
      el.scrollTo({ left: n * el.clientWidth, behavior: "smooth" });
    }, 3000);
    return () => clearInterval(id);
  }, [items.length]);

  useEffect(() => {
    const l = items[idx];
    if (l) track("ad_view", { ad: l.id, lid: l.id, cat: l.category });
  }, [idx, items]);

  if (!items.length) return null;
  const onScroll = (e) => setIdx(Math.round(e.currentTarget.scrollLeft / e.currentTarget.clientWidth));
  const pause = () => { hold.current = Date.now() + 6000; };
  return (
    <section className="px-4 mt-6" data-testid="sponsored-slider">
      <div ref={ref} onScroll={onScroll} onPointerDown={pause} onTouchStart={pause} className="flex overflow-x-auto no-scrollbar snap-x snap-mandatory rounded-3xl">
        {items.map((l) => <Slide key={l.id} l={l} onOpen={() => { track("ad_click", { ad: l.id, lid: l.id, cat: l.category }); nav(`/shpallje/${l.id}`); }} />)}
      </div>
      {items.length > 1 && (
        <div className="flex justify-center gap-2 mt-2.5">
          {items.map((l, i) => <span key={l.id} data-testid={`sponsored-dot-${i}`} className={`w-2.5 h-2.5 rounded-full transition-colors ${i === idx ? "bg-emerald-500" : "bg-slate-300"}`} />)}
        </div>
      )}
    </section>
  );
}

export default SponsoredSlider;
